import { useEffect, useRef } from 'react';
import type { Socket } from 'socket.io-client';

/**
 * Latency-tracker for unwind WS events. The backend stamps every emitted
 * payload with `_meta = { serverEmittedAt, seq }`. Each event we listen
 * to here gets the receive timestamp, and the first
 * `requestAnimationFrame` after the listener fires gets the render
 * timestamp — which is the cheapest accurate proxy for "the new state
 * is on the screen".
 *
 * Samples are batched and flushed every FLUSH_INTERVAL_MS via
 * `socket.emit('latency_report', { samples })`. The server computes
 * histogram percentiles and exposes them at `/unwind/metrics/latency`.
 *
 * Clock skew between client and server is unavoidable. Backend drops
 * negative `wsDeliveryMs` samples (clock ahead) and trusts only
 * `reactRenderMs` for skew-free numbers.
 */
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 200;

interface Sample {
  event: string;
  seq: number;
  serverEmittedAt: number;
  clientReceivedAt: number;
  renderedAt?: number;
}

export function recordReceived(
  buf: Sample[],
  event: string,
  payload: unknown,
): Sample | null {
  if (
    !payload ||
    typeof payload !== 'object' ||
    !('_meta' in payload)
  ) {
    return null;
  }
  const meta = (payload as { _meta: { serverEmittedAt?: number; seq?: number } })
    ._meta;
  if (
    typeof meta?.serverEmittedAt !== 'number' ||
    typeof meta?.seq !== 'number'
  ) {
    return null;
  }
  const sample: Sample = {
    event,
    seq: meta.seq,
    serverEmittedAt: meta.serverEmittedAt,
    clientReceivedAt: Date.now(),
  };
  buf.push(sample);
  // First rAF after the listener fires is "frame on which new state lands
  // in the layout pass" — close enough to perceived render latency for
  // our purposes without involving paint timing or component refs.
  requestAnimationFrame(() => {
    sample.renderedAt = Date.now();
  });
  if (buf.length > MAX_BATCH_SIZE) {
    buf.splice(0, buf.length - MAX_BATCH_SIZE);
  }
  return sample;
}

/**
 * Hook to start a periodic flush of buffered samples to the server.
 * Call once from the unwinding socket hook with the active socket and
 * a stable buffer ref.
 *
 * Alongside the per-event WS/render samples, each flush carries browser
 * long-task telemetry — the count + total blocking duration of
 * `PerformanceObserver`-reported long tasks (>50ms main-thread blocks)
 * accumulated since the previous flush — plus `document.visibilityState`
 * at flush time. The render-load fix is meant to cut these long tasks; the
 * visibility flag tells a real render stall from a backgrounded-tab
 * throttle (the browser throttles timers/rAF on hidden tabs, which can
 * also stall the PONG). Both are guarded for browser support and are
 * optional on the wire — the backend tolerates their absence.
 */
export function useLatencyTracker(
  socket: Socket | null,
  buf: { current: Sample[] },
): void {
  const flushRef = useRef<number | null>(null);
  // Long-task accumulators since the last flush. Reset on each emit.
  const longtaskCountRef = useRef(0);
  const longtaskMsRef = useRef(0);

  // Observe long tasks (>50ms main-thread blocks) for the lifetime of the
  // hook. Feature-detected: Safari and older browsers don't support the
  // 'longtask' entry type — no-op there. Disconnected on cleanup.
  useEffect(() => {
    if (typeof PerformanceObserver === 'undefined') return;
    const supported =
      (
        PerformanceObserver as unknown as {
          supportedEntryTypes?: string[];
        }
      ).supportedEntryTypes?.includes('longtask') ?? false;
    if (!supported) return;

    let observer: PerformanceObserver | null = null;
    try {
      observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          longtaskCountRef.current += 1;
          longtaskMsRef.current += entry.duration;
        }
      });
      observer.observe({ entryTypes: ['longtask'] });
    } catch {
      // Some browsers throw on an unsupported entry type even when
      // supportedEntryTypes claims otherwise — treat as unsupported.
      observer = null;
    }
    return () => {
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!socket) return;
    flushRef.current = window.setInterval(() => {
      if (!socket.connected) return;
      const samples = buf.current.splice(0, buf.current.length);
      const longtaskCount = longtaskCountRef.current;
      const longtaskMs = longtaskMsRef.current;
      // Nothing to report? Skip the emit entirely.
      if (samples.length === 0 && longtaskCount === 0) return;
      longtaskCountRef.current = 0;
      longtaskMsRef.current = 0;
      const visibility =
        typeof document !== 'undefined' ? document.visibilityState : undefined;
      try {
        socket.emit('latency_report', {
          samples,
          longtaskCount,
          longtaskMs: Math.round(longtaskMs),
          visibility,
        });
      } catch {
        // Drop samples silently — losing one batch is harmless.
      }
    }, FLUSH_INTERVAL_MS);
    return () => {
      if (flushRef.current !== null) {
        window.clearInterval(flushRef.current);
        flushRef.current = null;
      }
    };
  }, [socket, buf]);
}

export type { Sample as LatencySample };
